import isEqual from 'lodash/isEqual';

import {
  ListItem,
  ListItemProps,
  Select,
  SelectProps,
  Typography,
  Box,
  tokens,
} from '@cast/design-system';
import {
  NodeResourceOffering,
  NodeResourceOfferingWithSoleTenancy,
} from '@cast/types';

import { ResourceOfferingChip } from 'components/chips';
import { useIsSoleTenantOfferingVisible } from 'hooks/useIsSoleTenantOfferingVisible';

import {
  ClientSideSearchCriterion,
  makeEnumAggregateFn,
  useFiltersDropdownAggregate,
  useSearchCriterion,
} from '@cast/shared-search-ui';

type Props = Omit<
  SelectProps<
    NodeResourceOfferingWithSoleTenancy,
    NodeResourceOfferingWithSoleTenancy,
    'multiple'
  >,
  'options' | 'multiple'
> & {
  criterion: ClientSideSearchCriterion<any>;
};

const allResourceOfferings: Record<
  keyof typeof NodeResourceOffering | 'SOLE_TENANT',
  NodeResourceOfferingWithSoleTenancy
> = {
  ...NodeResourceOffering,
  SOLE_TENANT: 'sole-tenant',
};

export const NodeResourceOfferingFilter = ({ criterion, ...props }: Props) => {
  const _criterion = useSearchCriterion(criterion);
  const isSoleTenantOfferingVisible = useIsSoleTenantOfferingVisible();
  const { aggregate, dropdownProps } = useFiltersDropdownAggregate(
    criterion.key,
    makeEnumAggregateFn(allResourceOfferings, 'resourceOffering'),
    true
  );

  const resourceOfferingOptions: NodeResourceOfferingWithSoleTenancy[] = [
    NodeResourceOffering.SPOT,
    NodeResourceOffering.ON_DEMAND,
    ...(isSoleTenantOfferingVisible
      ? (['sole-tenant'] satisfies NodeResourceOfferingWithSoleTenancy[])
      : []),
    NodeResourceOffering.FALLBACK,
  ];

  return (
    <Select
      testId="node-list-resource-offering-filter"
      size="md"
      inputText="Resource offering"
      checkable
      multiple
      renderOption={(
        resourceOffering: NodeResourceOfferingWithSoleTenancy,
        itemProps: ListItemProps
      ) => (
        <ListItem {...itemProps}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap="8px"
          >
            <ResourceOfferingChip
              resourceOffering={resourceOffering}
              sx={{ pointerEvents: 'none' }}
            />
            <Typography
              variant="P12R"
              color={tokens.theme.fg.structural.secondary}
              width="3ch"
              textAlign="end"
            >
              {aggregate[resourceOffering]}
            </Typography>
          </Box>
        </ListItem>
      )}
      isOptionEqualToValue={isEqual}
      {...props}
      value={_criterion.value}
      onChange={(options) => {
        if (options?.length) {
          _criterion.setValue?.(options);
        } else {
          _criterion.clear?.();
        }
      }}
      options={resourceOfferingOptions}
      {...dropdownProps}
    />
  );
};
